/**
 * One illustration per feature row: a small product moment drawn in the studio's
 * own parts (frames, cards, chips, nodes), on white. Amber marks what is live.
 * Every visual is decorative; the row text carries the meaning.
 */
import type { FC, ReactNode } from 'react'
import type { FeatureId } from './features-data'

const IMG = {
  fashion: '/images/medium-fashion.jpg',
  swoosh: '/images/medium-photography.jpg',
  field: '/images/node-compositor.jpg',
  portrait: '/images/node-image.jpg',
} as const

const chip = 'rounded-md border border-line bg-card px-2 py-1 text-[10px] text-muted shadow-[0_1px_2px_rgba(2,2,2,0.06)]'
const cardCls = 'rounded-lg border border-line bg-card shadow-[0_6px_20px_rgba(2,2,2,0.08)]'
const noise =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"

function Stage({ children }: { children: ReactNode }) {
  return <div className="relative h-full w-full">{children}</div>
}

/** Denoising on your GPU: the frame is sharp above the scan line, still noise below it. */
const Local: FC = () => (
  <Stage>
    <div className={`absolute top-2 left-1/2 h-[176px] w-[140px] -translate-x-1/2 overflow-hidden ${cardCls}`}>
      <img src={IMG.fashion} alt="" className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-x-0 bottom-0 h-[36%] overflow-hidden">
        <img src={IMG.fashion} alt="" className="absolute bottom-0 h-[176px] w-full object-cover blur-[5px] saturate-50" />
        <div className="absolute inset-0 opacity-70 mix-blend-overlay" style={{ backgroundImage: noise }} />
      </div>
      <span className="absolute inset-x-0 bottom-[36%] h-px bg-lamp shadow-[0_0_10px_2px_rgba(242,165,22,0.6)]" />
    </div>
    <span className={`${chip} absolute top-6 left-2`}>Z-Image Turbo · local</span>
    <span className={`${chip} absolute right-2 bottom-8 flex items-center gap-1.5`}>
      <span className="ping size-1.5 rounded-full bg-lamp" />
      <span className="num text-bone">62%</span> step 5 / 8
    </span>
  </Stage>
)

const SLIDERS = [
  ['Steps', '30', 0.55, false],
  ['CFG', '6.5', 0.4, true],
  ['Creativity', '0.7', 0.7, false],
] as const

/** The advanced panel: model, three sliders (one being dragged), a locked seed. */
const Params: FC = () => (
  <Stage>
    <div className={`absolute top-1/2 left-1/2 w-[236px] -translate-x-1/2 -translate-y-1/2 p-3.5 ${cardCls}`}>
      <div className="flex items-center justify-between text-[10px]">
        <span className="uppercase tracking-wider text-faint">Parameters</span>
        <span className="text-muted">SDXL · local</span>
      </div>
      <div className="mt-3 flex flex-col gap-3">
        {SLIDERS.map(([name, val, pos, live]) => (
          <div key={name} className="flex items-center gap-2.5">
            <span className="w-[58px] text-[10.5px] text-muted">{name}</span>
            <span className="relative h-[3px] flex-1 rounded-full bg-line">
              <span className="absolute inset-y-0 left-0 rounded-full bg-bone" style={{ width: `${pos * 100}%` }} />
              <span
                className={`absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-card ${live ? 'border-lamp ring-4 ring-lamp/20' : 'border-bone'}`}
                style={{ left: `${pos * 100}%` }}
              />
            </span>
            <span className={`num w-7 text-right text-[10.5px] ${live ? 'text-lamp-ink' : 'text-bone'}`}>{val}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-dashed border-line pt-2.5 text-[10.5px]">
        <span className="text-muted">Seed</span>
        <span className="num text-bone">8812 · locked</span>
      </div>
    </div>
  </Stage>
)

/** A selected frame with a painted mask, and the action strip that edits it. */
const Tools: FC = () => (
  <Stage>
    <div className="absolute top-3 left-1/2 h-[124px] w-[200px] -translate-x-1/2">
      <img src={IMG.field} alt="" className="size-full rounded-[3px] object-cover" />
      <svg viewBox="0 0 210 140" className="absolute inset-0 size-full" aria-hidden>
        <path
          d="M118 22c16-6 34 2 38 18s-2 34 4 50-10 30-28 26-22-20-26-38-6-50 12-56z"
          fill="rgba(242,165,22,0.32)"
          stroke="#f2a516"
          strokeWidth="1.5"
          strokeDasharray="4 3"
        />
      </svg>
      <span className="pointer-events-none absolute -inset-[3px] rounded-[4px] border border-lamp" />
      {['-top-[5px] -left-[5px]', '-top-[5px] -right-[5px]', '-bottom-[5px] -left-[5px]', '-bottom-[5px] -right-[5px]'].map((p) => (
        <span key={p} className={`absolute size-2 border border-lamp bg-card ${p}`} />
      ))}
    </div>
    <div className={`absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1 p-1 ${cardCls}`}>
      {['Inpaint', 'Upscale', 'Remove BG', 'Vary'].map((t, i) => (
        <span key={t} className={`whitespace-nowrap rounded-md px-2 py-1 text-[10px] ${i === 0 ? 'bg-bone text-card' : 'text-muted'}`}>
          {t}
        </span>
      ))}
    </div>
  </Stage>
)

/** A design frame pulled apart into its layers: photo, backdrop, headline. */
const Layers: FC = () => (
  <Stage>
    <div className="absolute top-[58%] left-[36%] h-[132px] w-[132px] -translate-x-1/2 -translate-y-1/2 [perspective:600px]">
      <div className="relative size-full [transform:rotateX(55deg)_rotateZ(-40deg)] [transform-style:preserve-3d]">
        <div className="absolute inset-0 rounded-[3px] border border-line bg-[#e4e2df] [transform:translateZ(0px)]" />
        <img src={IMG.portrait} alt="" className="absolute inset-3 size-[calc(100%-24px)] rounded-[2px] object-cover shadow-md [transform:translateZ(44px)]" />
        <div className="absolute inset-x-4 bottom-5 flex items-center gap-1 rounded-[2px] bg-bone px-2 py-1.5 shadow-md [transform:translateZ(90px)]">
          <span className="text-[13px] font-black tracking-tight text-card">SS27</span>
          <span className="ml-auto h-1.5 w-8 rounded-full bg-lamp" />
        </div>
      </div>
    </div>
    <ul className={`absolute top-1/2 right-2 w-[112px] -translate-y-1/2 p-1.5 ${cardCls}`}>
      {[
        ['T', 'Headline', true],
        ['▣', 'Portrait', false],
        ['■', 'Backdrop', false],
      ].map(([k, v, on]) => (
        <li key={String(v)} className={`flex items-center gap-2 rounded-md px-1.5 py-1 text-[10px] ${on ? 'bg-lamp/15 text-bone' : 'text-muted'}`}>
          <span className="w-3 text-center text-faint">{k}</span>
          {v}
        </li>
      ))}
    </ul>
  </Stage>
)

const VERSIONS = [
  { v: 'v1', f: 'grayscale contrast-125', x: 6 },
  { v: 'v2', f: 'sepia-[0.6]', x: 96 },
  { v: 'v3', f: '', x: 186, on: true },
] as const

/** Lineage: the same frame through three versions, with a branch off v2. */
const Versions: FC = () => (
  <Stage>
    <div className="absolute top-1/2 left-1/2 h-[168px] w-[262px] -translate-x-1/2 -translate-y-1/2">
      <svg viewBox="0 0 262 168" className="absolute inset-0 size-full" aria-hidden>
        <g fill="none" stroke="rgba(2,2,2,0.3)" strokeWidth="1.2">
          <path d="M76 52h20M166 52h20" />
          <path d="M131 92v22c0 14 8 20 22 20h10" />
        </g>
      </svg>
      {VERSIONS.map((v) => (
        <div key={v.v} className="absolute top-2" style={{ left: v.x }}>
          <img
            src={IMG.swoosh}
            alt=""
            className={`h-[80px] w-[70px] rounded-[3px] object-cover ${v.f} ${'on' in v ? 'ring-2 ring-lamp ring-offset-2' : ''}`}
          />
          <span className={`mt-1 block text-center text-[10px] ${'on' in v ? 'text-lamp-ink' : 'text-faint'}`}>{v.v}</span>
        </div>
      ))}
      <div className="absolute top-[112px] left-[166px] flex items-center gap-2">
        <img src={IMG.swoosh} alt="" className="h-[44px] w-[38px] rounded-[3px] object-cover hue-rotate-[160deg]" />
        <span className="whitespace-nowrap text-[10px] text-faint">v2b · branch</span>
      </div>
    </div>
  </Stage>
)

/** An automation: a watched folder feeds a generate block that is running, then saves. */
const Graph: FC = () => (
  <Stage>
    <svg viewBox="0 0 300 180" className="absolute inset-0 size-full" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d="M86 70 C 106 70, 106 90, 120 90" fill="none" stroke="rgba(2,2,2,0.3)" strokeWidth="1.2" />
      <path d="M198 90 C 212 90, 212 110, 226 110" fill="none" stroke="#f2a516" strokeWidth="1.4" strokeDasharray="4 3" />
    </svg>
    <div className="absolute inset-0 m-auto h-[180px] w-[300px]">
      <div className={`absolute top-[46px] left-0 w-[86px] p-2 ${cardCls}`}>
        <span className="text-[9px] uppercase tracking-wider text-faint">Trigger</span>
        <p className="mt-1 text-[10.5px] text-bone">Watch folder</p>
        <p className="num mt-0.5 text-[9.5px] text-muted">~/inbox</p>
      </div>
      <div className={`absolute top-[18px] left-[120px] w-[78px] overflow-hidden border-lamp p-1.5 ${cardCls}`}>
        <span className="text-[9px] uppercase tracking-wider text-lamp-ink">Generate</span>
        <img src={IMG.portrait} alt="" className="mt-1 h-[84px] w-full rounded-[2px] object-cover" />
        <span className="mt-1.5 block h-[2px] w-full bg-line">
          <span className="block h-full w-[62%] bg-lamp" />
        </span>
      </div>
      <div className={`absolute top-[86px] left-[226px] w-[74px] p-2 ${cardCls}`}>
        <span className="text-[9px] uppercase tracking-wider text-faint">Save</span>
        <p className="num mt-1 text-[10px] text-bone">~/out/</p>
      </div>
    </div>
  </Stage>
)

const CALLS = [
  ['Inspect canvas', 'done'],
  ['Generate 3D model', 'done'],
  ['Render 3D views', 'running'],
] as const

/** The agent at work: a request, then the tool calls it makes, one still running. */
const Agent: FC = () => (
  <Stage>
    <div className="absolute inset-0 m-auto flex h-[172px] w-[250px] flex-col gap-2">
      <div className="ml-auto max-w-[190px] rounded-xl bg-[#e4e2df] px-3 py-2 text-[10.5px] leading-snug text-bone">
        Turn this lamp sketch into a 3D model and render three angles.
      </div>
      <ul className={`flex flex-col p-1.5 ${cardCls}`}>
        {CALLS.map(([t, st]) => (
          <li key={t} className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5">
            <span className="text-[10.5px] text-bone">{t}</span>
            {st === 'running' ? (
              <span className="flex items-center gap-1.5 text-[9.5px] text-lamp-ink">
                running <span className="ping size-1.5 rounded-full bg-lamp" />
              </span>
            ) : (
              <span className="text-[9.5px] text-faint">✓ done</span>
            )}
          </li>
        ))}
      </ul>
      <span className="text-[9.5px] text-faint">Genie’s agent on Claude, or local with Ollama</span>
    </div>
  </Stage>
)

const MODELS = [
  { m: 'Z-Image Turbo', src: 'Local · free', cloud: false },
  { m: 'FLUX.2 pro', src: 'Genie credits', cloud: true },
  { m: 'Kling v3 video', src: 'fal.ai · your key', cloud: true },
] as const

/** The model picker: local models beside cloud ones that run on your own key. */
const Cloud: FC = () => (
  <Stage>
    <div className={`absolute top-1/2 left-1/2 w-[240px] -translate-x-1/2 -translate-y-1/2 p-1.5 ${cardCls}`}>
      <span className="block px-2 pt-1 pb-1.5 text-[9px] uppercase tracking-wider text-faint">Model</span>
      {MODELS.map((x, i) => (
        <div key={x.m} className={`flex items-center justify-between rounded-md px-2 py-2 ${i === 1 ? 'bg-lamp/15' : ''}`}>
          <span className="flex items-center gap-2 text-[11px] text-bone">
            <span className={`size-1.5 rounded-full ${x.cloud ? 'bg-lamp' : 'bg-[#2f9e62]'}`} />
            {x.m}
          </span>
          <span className="text-[9.5px] text-muted">{x.src}</span>
        </div>
      ))}
    </div>
  </Stage>
)

export const FEATURE_VISUALS: Record<FeatureId, FC> = {
  local: Local,
  params: Params,
  tools: Tools,
  layers: Layers,
  versions: Versions,
  graph: Graph,
  agent: Agent,
  cloud: Cloud,
}
